import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useSession } from "@/auth/SessionProvider";
import { CrmLeadModuleChannelFieldDetailPanel } from "@/components/crm/CrmLeadModuleChannelFieldDetailPanel";
import {
  CRM_LEAD_CHANNEL_FIELD_GROUP_IDS,
  CRM_LEAD_CHANNEL_FIELD_GROUP_LABELS,
  CRM_LEAD_FIELD_SECTION_LABELS,
  crmLeadFieldSectionIdFromSlug,
  type CrmLeadChannelFieldGroupId,
  type CrmLeadModuleFieldsDocument,
} from "@/lib/crmLeadModuleFields";
import {
  fetchCrmLeadModuleFields,
  saveCrmLeadModuleFields,
  seedStandardCrmLeadModuleFields,
} from "@/lib/crmLeadModuleFieldsApi";

function canManageCrmSettings(role: string | undefined): boolean {
  return role === "super_admin" || role === "account_owner" || role === "account_admin";
}

function parseGroupId(raw: string | undefined): CrmLeadChannelFieldGroupId | null {
  const id = (raw || "").toLowerCase();
  return CRM_LEAD_CHANNEL_FIELD_GROUP_IDS.includes(id as CrmLeadChannelFieldGroupId)
    ? (id as CrmLeadChannelFieldGroupId)
    : null;
}

export function CrmLeadModuleFieldDetailPage() {
  const { sectionSlug, groupId: groupIdParam } = useParams<{ sectionSlug: string; groupId: string }>();
  const session = useSession();
  const userRole = session.data?.user?.role ?? undefined;
  const canManage = canManageCrmSettings(userRole);

  const sectionId = useMemo(() => crmLeadFieldSectionIdFromSlug(sectionSlug ?? ""), [sectionSlug]);
  const groupId = useMemo(() => parseGroupId(groupIdParam), [groupIdParam]);

  const [fieldSections, setFieldSections] = useState<CrmLeadModuleFieldsDocument["sections"]>(
    seedStandardCrmLeadModuleFields(),
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldsApiLive, setFieldsApiLive] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const fieldsResult = await fetchCrmLeadModuleFields();
      if (!cancelled) {
        setFieldSections(fieldsResult.sections);
        setFieldsApiLive(fieldsResult.apiLive);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!sectionId || !groupId || sectionId === "lead_status") {
    return <Navigate to="/crm/settings/modules/lead?tab=fields" replace />;
  }

  const sectionLabel = CRM_LEAD_FIELD_SECTION_LABELS[sectionId];
  const groupLabel = CRM_LEAD_CHANNEL_FIELD_GROUP_LABELS[groupId];

  const onSave = async () => {
    if (!canManage) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    const result = await saveCrmLeadModuleFields(fieldSections);
    setSaving(false);
    if (!result.ok) {
      setFieldsApiLive(false);
      setError(result.error || "Could not save field definitions.");
      return;
    }
    setFieldsApiLive(true);
    if (result.sections) setFieldSections(result.sections);
    setMessage(`Saved ${groupLabel} options for ${sectionLabel}.`);
  };

  return (
    <div className="mx-auto flex min-h-0 w-full max-w-4xl flex-1 flex-col gap-4 overflow-y-auto px-4 py-5">
      <Link
        to="/crm/settings/modules/lead?tab=fields"
        className="inline-flex w-fit items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-300"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Lead fields
      </Link>

      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{sectionLabel}</p>
        <h1 className="mt-1 text-2xl font-semibold text-slate-900 dark:text-white">{groupLabel}</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Manage {groupLabel.toLowerCase()} types and defaults for this section.
        </p>
      </div>

      {!fieldsApiLive ? (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Lead Fields API is not live yet. Changes are kept locally until the API route is deployed.
        </p>
      ) : null}

      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-zinc-950">
        {loading ? <p className="text-sm text-slate-500">Loading…</p> : null}
        {!loading ? (
          <>
            <CrmLeadModuleChannelFieldDetailPanel
              sectionId={sectionId}
              groupId={groupId}
              sections={fieldSections}
              onChange={setFieldSections}
              canManage={canManage}
              disabled={saving}
            />
            {canManage ? (
              <button
                type="button"
                disabled={saving}
                onClick={() => void onSave()}
                className="mt-4 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            ) : null}
          </>
        ) : null}
        {error ? (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {error}
          </div>
        ) : null}
        {message ? (
          <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
            {message}
          </div>
        ) : null}
      </section>
    </div>
  );
}
